import { useEffect, useState } from "react";
import {
    Box,
    Chip,
    Typography,
    Alert,
    Tooltip,
    Tabs,
    TabList,
    Tab,
    TabPanel,
    ListItemDecorator,
    tabClasses,
    Switch,
    Card,
    CardContent,
} from "@mui/joy";
import { Clock, Info, PackageCheck, Package, Wrench } from "lucide-react";
import moment from "moment";

import { Materials } from "./Materials";
import { LaborComponent } from "./LaborComponent";
import TaskActionComponent from "../../../components/Tasks/components/TaskAction";

interface ITaskComponent {
    activeTask: any;
    measurements: any;
    onComplete: () => void;
    actionProgress: any;
}

const taskDetails = `The primary goal of the Work Order Creation stage is to formalize the scope of work, define
                            project requirements, and allocate necessary resources. This stage ensures clear
                            communication between stakeholders, outlines tasks and timelines, and serves as the
                            foundation for executing roofing repairs, maintenance, or installations efficiently.`;

function TaskDetails() {
    return (
        <Box sx={{ minWidth: 300, maxWidth: "100%", width: 400, p: 1 }}>
            <Typography>{taskDetails}</Typography>
        </Box>
    );
}

export default function TaskComponent(props: ITaskComponent) {
    const { activeTask, measurements, onComplete, actionProgress } = props;
    const { CreatedAt, Status } = activeTask;

    const [isNextStageEligible, setIsNextStageEligible] = useState({ isSupplierSelected: false });
    const [overrideCalculations, setOverrideCalculations] = useState(true);

    useEffect(() => {
        const scrollableDiv = document.getElementById("project-details-parent");
        if (scrollableDiv) {
            //scrollableDiv.scrollTop = 0; // or use scrollTo
            scrollableDiv.scrollTo({ top: 0, behavior: "smooth" });
        }
    }, []);

    const isNextStageReady = Object.values(isNextStageEligible).every((value) => value === true);

    return (
        <Box>
            <Box mt={2} ml={1} mr={1}>
                <Typography startDecorator={<PackageCheck size={16} />} level="body-sm" gutterBottom>
                    Task
                </Typography>
                <Typography
                    level="h4"
                    sx={{ fontWeight: 500, display: "flex", alignItems: "center", gap: 0.5 }}
                    component={"div"}
                >
                    Estimate/Contract
                    <Tooltip
                        arrow
                        title={<TaskDetails />}
                        variant="outlined"
                        sx={{ border: "1px solid", borderColor: "divider" }}
                    >
                        <Info size={12} />
                    </Tooltip>
                </Typography>

                <Box mt={2} display={"flex"} gap={1}>
                    <Chip
                        color={Status === "COMPLETED" ? "success" : "warning"}
                        sx={{ fontSize: 12, border: "1px solid", borderColor: "divider" }}
                    >
                        {Status}
                    </Chip>
                    <Chip color="neutral" variant="outlined" sx={{ fontSize: 12 }} startDecorator={<Clock size={14} />}>
                        Created On: {moment(CreatedAt).format("MM/DD/YYYY")}
                    </Chip>
                </Box>

                <Card sx={{ mt: 2, mb: 1 }}>
                    <CardContent>
                        <Typography level="title-sm">Quick Settings</Typography>
                        <Typography level="body-sm">Auto-update quantities on change</Typography>
                        <Box display="flex" alignItems="center" gap={1} mt={1}>
                            <Switch
                                checked={overrideCalculations}
                                onChange={(event) => setOverrideCalculations(event.target.checked)}
                                sx={{
                                    "--Switch-thumbSize": "16px",
                                    "--Switch-trackWidth": "34px",
                                    "--Switch-trackHeight": "20px",
                                }}
                            />
                            <Typography level="body-sm" sx={{ userSelect: "none" }}>
                                Auto-update quantities on change
                            </Typography>
                        </Box>
                    </CardContent>
                </Card>

                <Box mt={2}>
                    <Tabs defaultValue="materials" sx={{ width: "100%" }}>
                        <TabList
                            sx={{
                                gap: 2,
                                [`&& .${tabClasses.root}`]: {
                                    flex: "initial",
                                    color: "text.secondary",
                                    fontWeight: 600,
                                    opacity: 0.7,
                                    paddingLeft: 0.5,
                                    paddingRight: 0.5,
                                    paddingBottom: 1.5,
                                    paddingTop: 1.5,
                                    background: "transparent",
                                    bgcolor: "transparent",
                                    "&:hover": {
                                        bgcolor: "transparent",
                                    },
                                    [`&.${tabClasses.selected}`]: {
                                        color: "primary.plainColor",
                                        fontWeight: 600,
                                        opacity: 1,
                                        "&::after": {
                                            height: 2,
                                            borderTopLeftRadius: 3,
                                            borderTopRightRadius: 3,
                                            bgcolor: "primary.500",
                                        },
                                    },
                                },
                            }}
                        >
                            <Tab value="materials">
                                <ListItemDecorator>
                                    <Package size={16} />
                                </ListItemDecorator>
                                Materials
                            </Tab>
                            <Tab value="labor">
                                <ListItemDecorator>
                                    <Wrench size={16} />
                                </ListItemDecorator>
                                Labor
                            </Tab>
                        </TabList>

                        <TabPanel value="materials" sx={{ px: 0, pt: 2 }}>
                            <Typography level={"body-sm"} sx={{ color: "text.secondary", mb: 2 }}>
                                Material quantities are automatically determined based on the roof area and other
                                factors. Manual modifications are not recommended to ensure accuracy in estimations and
                                cost calculations.
                            </Typography>
                            <Materials
                                measurements={measurements}
                                setIsNextStageEligible={setIsNextStageEligible}
                                overrideCalculations={overrideCalculations}
                            />
                        </TabPanel>

                        <TabPanel value="labor" sx={{ px: 0, pt: 2 }}>
                            <Typography level={"body-sm"} sx={{ color: "text.secondary", mb: 2 }}>
                                Labor items are automatically identified based on project measurements. Select a
                                supplier to view pricing.
                            </Typography>
                            <LaborComponent
                                measurements={measurements}
                                setIsNextStageEligible={setIsNextStageEligible}
                                overrideCalculations={overrideCalculations}
                            />
                        </TabPanel>
                    </Tabs>
                </Box>
                <Box mt={2}>
                    {!isNextStageEligible.isSupplierSelected ? (
                        <Alert
                            variant="soft"
                            color="danger"
                            startDecorator={<Info size={18} style={{ marginTop: 4 }} />}
                            sx={{
                                border: "1px solid",
                                borderColor: "--standard-palette-danger-softColor",
                                alignItems: "flex-start",
                            }}
                        >
                            <div>
                                <Typography level="title-sm">Supplier selection is required</Typography>
                                <Typography level="body-sm">
                                    Please select a supplier from the supplier comparison section above to proceed to
                                    the next task. Click the "Compare & Choose" button to view available suppliers and
                                    make your selection.
                                </Typography>
                            </div>
                        </Alert>
                    ) : (
                        <Alert
                            variant="soft"
                            color="success"
                            startDecorator={<Info size={18} style={{ marginTop: 4 }} />}
                            sx={{
                                border: "1px solid",
                                borderColor: "--standard-palette-success-softColor",
                                alignItems: "flex-start",
                            }}
                        >
                            <div>
                                <Typography level="title-sm">Supplier selected</Typography>
                                <Typography level="body-sm">
                                    You have selected a supplier. You can now proceed to the next task. Order placement
                                    will be enabled once the workflow progresses to the Material Ordering stage.
                                </Typography>
                            </div>
                        </Alert>
                    )}
                </Box>
                <TaskActionComponent
                    taskName={"Estimate/Contract"}
                    onComplete={onComplete}
                    actionProgress={actionProgress}
                    isNextEligible={isNextStageReady}
                />
            </Box>
        </Box>
    );
}
